export interface DocumentTextQuality {
  reliable: boolean;
  reason: 'ok' | 'too_short' | 'invalid_characters' | 'symbol_heavy' | 'garbled_tokens';
}

const PAGE_HEADING = /--- หน้า \d+ ---/g;
const TEXT_CHARACTER = /[\p{L}\p{N}\p{M}]/u;
const COMMON_PUNCTUATION = /[.,:;!?%+\-–—/()'"“”‘’=…ฯๆ฿]/u;
const SUSPICIOUS_CHARACTER = /[@|\\<>\[\]{}_`~^]/u;

/**
 * Detect a broken PDF text layer before it is accepted as searchable text.
 * Some Thai PDFs draw correct glyphs while exposing unrelated character codes;
 * those strings are often long, so length alone is not a useful quality check.
 */
export function assessDocumentTextQuality(input: string): DocumentTextQuality {
  const text = input.replace(PAGE_HEADING, ' ').trim();
  const compact = [...text].filter((character) => !/\s/u.test(character));
  if (compact.length < 40) return { reliable: false, reason: 'too_short' };

  const hasInvalidCharacter = [...text].some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint === 0xfffd ||
      (codePoint <= 0x1f && codePoint !== 0x09 && codePoint !== 0x0a && codePoint !== 0x0d) ||
      (codePoint >= 0x7f && codePoint <= 0x9f) ||
      (codePoint >= 0xe000 && codePoint <= 0xf8ff);
  });
  if (hasInvalidCharacter) return { reliable: false, reason: 'invalid_characters' };

  let textCount = 0;
  let suspiciousCount = 0;
  let uncommonCount = 0;
  for (const character of compact) {
    if (TEXT_CHARACTER.test(character)) textCount += 1;
    else if (SUSPICIOUS_CHARACTER.test(character)) suspiciousCount += 1;
    else if (!COMMON_PUNCTUATION.test(character)) uncommonCount += 1;
  }

  const symbolRatio = (suspiciousCount * 2 + uncommonCount) / compact.length;
  const textRatio = textCount / compact.length;
  if (textRatio < 0.62 || symbolRatio > 0.12 || suspiciousCount / compact.length > 0.025) {
    return { reliable: false, reason: 'symbol_heavy' };
  }

  const tokens = text.split(/\s+/u).filter(Boolean);
  const garbledTokens = tokens.filter((token) => {
    if (token.length < 6) return false;
    const characters = [...token];
    const noisy = characters.filter((character) =>
      SUSPICIOUS_CHARACTER.test(character) || (!TEXT_CHARACTER.test(character) && !COMMON_PUNCTUATION.test(character)),
    ).length;
    return noisy / characters.length > 0.25;
  }).length;
  if (tokens.length >= 4 && garbledTokens / tokens.length > 0.15) {
    return { reliable: false, reason: 'garbled_tokens' };
  }

  return { reliable: true, reason: 'ok' };
}
